/**
 * Root component. Routes by auth status — `.checking` shows a brief
 * splash, `.signedOut` routes to /sign-in, `.signedIn` users get the
 * full app shell with tab nav.
 */

import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { AppShell } from "@/shared/components/layout/AppShell";
import { DailyChallengeCelebrationProvider } from "@/features/daily/components/DailyChallengeCelebration";
import { FirstLeakReportModalProvider } from "@/features/billing/components/FirstLeakReportModal";
import { UpgradeModalProvider } from "@/features/billing/components/UpgradeModal";
import { WelcomeModalProvider } from "@/shared/components/WelcomeModal";
import { AnnouncementModalProvider } from "@/shared/components/AnnouncementModal";
import { AchievementUnlockProvider } from "@/shared/components/AchievementUnlockModal";
import { FirstReportNudgeProvider } from "@/features/onboarding/components/FirstReportNudgeProvider";
import { AuthProvider, useAuth } from "@/features/auth/lib/authContext";
import { useIsAdmin } from "@/features/admin/lib/useIsAdmin";
import { AdminLayout } from "@/features/admin/routes/AdminLayout";
import { OverviewView } from "@/features/admin/routes/OverviewView";
import { ReportedSpotsView } from "@/features/admin/routes/ReportedSpotsView";
import { FeedbackView as AdminFeedbackView } from "@/features/admin/routes/FeedbackView";
import { SpotsView } from "@/features/admin/routes/SpotsView";
import { UsersView } from "@/features/admin/routes/UsersView";
import { AnnouncementsView } from "@/features/admin/routes/AnnouncementsView";
import { BillingSuccess } from "@/features/billing/routes/BillingSuccess";
import { Chat } from "@/features/chat/routes/Chat";
import { Drill } from "@/features/drills/routes/Drill";
import { DrillSelector } from "@/features/drills/routes/DrillSelector";
import { Drills } from "@/features/drills/routes/Drills";
import { Feedback } from "@/features/feedback/routes/Feedback";
import { Glossary } from "@/features/study/routes/Glossary";
import { Pricing } from "@/features/billing/routes/Pricing";
import { SkillsLayout } from "@/features/skills/routes/SkillsLayout";
import { StandingView } from "@/features/skills/routes/StandingView";
import { FocusView } from "@/features/skills/routes/FocusView";
import { JourneyView } from "@/features/skills/routes/JourneyView";
import { GamificationView } from "@/features/gamification/routes/GamificationView";
import { QuizBbDefend } from "@/features/quiz/routes/QuizBbDefend";
import { QuizBluffRatio } from "@/features/quiz/routes/QuizBluffRatio";
import { QuizCostOfError } from "@/features/quiz/routes/QuizCostOfError";
import { QuizCountOuts } from "@/features/quiz/routes/QuizCountOuts";
import { QuizExploitDirection } from "@/features/quiz/routes/QuizExploitDirection";
import { QuizHandRole } from "@/features/quiz/routes/QuizHandRole";
import { QuizIdentifyArchetype } from "@/features/quiz/routes/QuizIdentifyArchetype";
import { QuizMdf } from "@/features/quiz/routes/QuizMdf";
import { QuizOpenFold } from "@/features/quiz/routes/QuizOpenFold";
import { QuizPickSizing } from "@/features/quiz/routes/QuizPickSizing";
import { QuizPositionOrdering } from "@/features/quiz/routes/QuizPositionOrdering";
import { QuizPotOdds } from "@/features/quiz/routes/QuizPotOdds";
import { QuizRangeAdvantage } from "@/features/quiz/routes/QuizRangeAdvantage";
import { QuizReversionTrigger } from "@/features/quiz/routes/QuizReversionTrigger";
import { QuizRuleOf42 } from "@/features/quiz/routes/QuizRuleOf42";
import { QuizSpr } from "@/features/quiz/routes/QuizSpr";
import { QuizWiderNarrower } from "@/features/quiz/routes/QuizWiderNarrower";
import { QuizzesHub } from "@/features/quiz/routes/QuizzesHub";
import { Recent } from "@/features/chat/routes/Recent";
import { Settings } from "@/features/auth/routes/Settings";
import { SheetReader } from "@/features/study/routes/SheetReader";
import { SignIn } from "@/features/auth/routes/SignIn";
import { SkillDetail } from "@/features/skills/routes/SkillDetail";
import { Study } from "@/features/study/routes/Study";
import { TrackerHome } from "@/features/tracker/routes/TrackerHome";
import { SessionDetail } from "@/features/tracker/routes/SessionDetail";
import { AnalyzerHome } from "@/features/analyzer/routes/AnalyzerHome";
import { HandBuilder } from "@/features/analyzer/routes/HandBuilder";

function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status.kind === "checking") return <Splash />;
  if (status.kind === "signedOut") {
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

/** Owner-only gate for /admin. UX guard only — every /admin/* backend call
 *  is independently gated by `require_admin`, so a non-admin who forces the
 *  route still gets 403s and sees nothing. While the `["me"]` query is in
 *  flight we show the splash; non-admins are bounced to /drills. */
function RequireAdmin() {
  const { isAdmin, isLoading } = useIsAdmin();
  if (isLoading) return <Splash />;
  if (!isAdmin) return <Navigate to="/drills" replace />;
  return <Outlet />;
}

/** Preserves the :skillId param when redirecting an old /profile/skill/:skillId
 *  URL to its new /skills/:skillId location. */
function ProfileSkillRedirect() {
  const { skillId } = useParams<{ skillId: string }>();
  return <Navigate to={`/skills/${skillId ?? ""}`} replace />;
}

function RedirectIfSignedIn({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status.kind === "checking") return <Splash />;
  if (status.kind === "signedIn") return <Navigate to="/drills" replace />;
  return <>{children}</>;
}

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-text-muted text-sm">Loading…</div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <UpgradeModalProvider>
        <WelcomeModalProvider>
        <FirstLeakReportModalProvider>
        <FirstReportNudgeProvider>
        <DailyChallengeCelebrationProvider>
        <AchievementUnlockProvider>
        <AnnouncementModalProvider>
        <Routes>
          <Route
            path="/sign-in"
            element={
              <RedirectIfSignedIn>
                <SignIn />
              </RedirectIfSignedIn>
            }
          />

          <Route element={<RequireAuth />}>
            <Route element={<AppShell />}>
              <Route index element={<Navigate to="/drills" replace />} />
              <Route path="/drills" element={<Drills />} />
              <Route path="/drills/:mode" element={<DrillSelector />} />
              <Route path="/study" element={<Study />} />
              <Route path="/study/:sheetId" element={<SheetReader />} />
              <Route path="/glossary" element={<Glossary />} />
              {/* /quizzes is the hub now that we have multiple quizzes.
                  Each quiz keeps its own stable URL — `/quizzes/<slug>`. */}
              <Route path="/quizzes" element={<QuizzesHub />} />
              <Route path="/quizzes/open-fold" element={<QuizOpenFold />} />
              <Route path="/quizzes/pot-odds" element={<QuizPotOdds />} />
              <Route path="/quizzes/count-outs" element={<QuizCountOuts />} />
              <Route path="/quizzes/rule-of-4-2" element={<QuizRuleOf42 />} />
              <Route path="/quizzes/bluff-ratio" element={<QuizBluffRatio />} />
              <Route path="/quizzes/mdf" element={<QuizMdf />} />
              <Route path="/quizzes/spr" element={<QuizSpr />} />
              <Route path="/quizzes/wider-narrower" element={<QuizWiderNarrower />} />
              <Route path="/quizzes/position-ordering" element={<QuizPositionOrdering />} />
              <Route path="/quizzes/bb-defend" element={<QuizBbDefend />} />
              <Route path="/quizzes/pick-sizing" element={<QuizPickSizing />} />
              <Route path="/quizzes/range-advantage" element={<QuizRangeAdvantage />} />
              <Route path="/quizzes/identify-archetype" element={<QuizIdentifyArchetype />} />
              <Route path="/quizzes/exploit-direction" element={<QuizExploitDirection />} />
              <Route path="/quizzes/hand-role" element={<QuizHandRole />} />
              <Route path="/quizzes/reversion-trigger" element={<QuizReversionTrigger />} />
              <Route path="/quizzes/cost-of-error" element={<QuizCostOfError />} />
              <Route path="/skills" element={<SkillsLayout />}>
                <Route index element={<StandingView />} />
                <Route path="focus" element={<FocusView />} />
                <Route path="journey" element={<JourneyView />} />
                <Route path="level" element={<GamificationView />} />
              </Route>
              <Route path="/skills/:skillId" element={<SkillDetail />} />
              <Route path="/tracker" element={<TrackerHome />} />
              <Route path="/tracker/:id" element={<SessionDetail />} />
              <Route path="/analyzer" element={<AnalyzerHome />} />
              <Route path="/analyzer/new" element={<HandBuilder />} />
              {/* Back-compat: old bookmarks land on /skills. */}
              <Route path="/profile" element={<Navigate to="/skills" replace />} />
              <Route path="/profile/skill/:skillId" element={<ProfileSkillRedirect />} />
              <Route path="/recent" element={<Recent />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/chat/:id" element={<Chat />} />
              {/* Focused single-spot drill surface. New sessions only;
                  resume + "walk through the reasoning" stay on /chat/:id. */}
              <Route path="/drill/new" element={<Drill />} />
              {/* Owner-only admin dashboard. Gated by RequireAdmin (UX) +
                  server-side require_admin on every /admin/* endpoint. */}
              <Route element={<RequireAdmin />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<OverviewView />} />
                  <Route path="users" element={<UsersView />} />
                  <Route path="spots" element={<SpotsView />} />
                  <Route path="announce" element={<AnnouncementsView />} />
                  <Route path="reported" element={<ReportedSpotsView />} />
                  <Route path="feedback" element={<AdminFeedbackView />} />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AnnouncementModalProvider>
        </AchievementUnlockProvider>
        </DailyChallengeCelebrationProvider>
        </FirstReportNudgeProvider>
        </FirstLeakReportModalProvider>
        </WelcomeModalProvider>
        </UpgradeModalProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
